import {
  ContentSwitcher,
  Field,
  IconButton,
  Scrollbar,
  SingleSelectDropdown,
  Text,
  type SingleSelectDropdownOption,
} from "@carbon6/c6-frontend-design";
import { useId, type ReactNode } from "react";
import {
  isUserProposal,
  EPISODE_LABEL_KEYS,
  RESERVED_KEYS,
  type ConfigCandidateView,
  type ConfigurationView,
  type DatasetDetails,
  type Dataset,
  type DatasetStructure,
  type ReservedKeyContract,
  type UnresolvedReference,
} from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { formatDateTime, parseUtcMs } from "@/utils/datetime";
import { EmbodimentDetailContent, formatTip } from "@/components/embodiment";
import { ChangeSummary, DiffRows, ModifiedMark } from "@/components/dataset/diff";
import type { LoadState } from "@/components/dataset/loadState";
import { EpisodeQualityCard } from "./EpisodeQualityCard";
import { MetadataCard } from "./MetadataCard";
import { DatasetInfoCard } from "../detail/DatasetInfoCard";
import { DatasetProcessingCard } from "../detail/DatasetProcessingCard";
import { DatasetOverviewPanel } from "../detail/DatasetOverviewPanel";
import { needsVideoFps } from "../detail/DatasetCameraTable";
import { configurationDiff, DIFF_AXIS } from "./reviewDiffModel";
import {
  AddLabelRow,
  HatLabelChip,
  OpenLabelMultiInput,
  RESERVED_KEY_UI,
  reservedControlKind,
  reservedKeyLabel,
  reservedValueOptions,
} from "@/components/label";
import { HatTextTooltip } from "@/components/tooltip";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import {
  draftFromConfiguration,
  labelSignature,
  labelsForKey,
  type DraftConfiguration,
  type DraftLabel,
} from "./datasetConfigurationDraft";
import type { DatasetReviewBinding } from "./useDatasetReview";
import styles from "./DatasetReviewPanel.module.css";

// 설정 조회 실패 안내의 마지막 줄. 사유 문장과 줄을 나눈다.
const RETRY_LATER = "잠시 후 다시 시도해 주세요.";

// 서버 사유에 같은 안내가 붙어 오면 떼어 낸다. 안내는 다음 줄에 한 번만 둔다.
function loadFailureReason(message: string | null): string {
  return (message ?? "").replace(RETRY_LATER, "").trim();
}

// draft 는 새 (key,value) 를 서버 get-or-create 대상으로 보낸다. 컨트롤은 카탈로그 uuid 를 모른다.
function toDraftLabel(key: string, value: string): DraftLabel {
  return { kind: "new", key, value };
}

// 카드 제목과 같은 문구로 그린다. 축이 자기 카드를 가지면 카드 제목이 곧 축 이름이다. 그래서
// 카드 안에서 이름을 다시 표시하지 않는다.
function CardTitle({ children }: { children: ReactNode }): ReactNode {
  return <Text variant={hatcheryTextModes["caption-bold"]}>{children}</Text>;
}

// reserved key 한 축의 편집 컨트롤. 컨트롤 종류의 SoT 는 RESERVED_KEY_UI(FE product 결정)다.
// 허용값의 SoT 는 계약(contract.values)이다. 확정 configuration 에서 예약 라벨은 optional 이다.
// 그래서 개수 사유는 붙이지 않는다. 필수 판정은 업로드 단계(`upload_required`)가 소유한다.
function ReservedLabelControl({
  contract,
  values,
  busy,
  onChange,
}: {
  contract: ReservedKeyContract;
  values: readonly string[];
  busy: boolean;
  onChange: (values: readonly string[]) => void;
}): ReactNode {
  const label = reservedKeyLabel(contract.key);
  const options = reservedValueOptions(contract.key, contract.values);
  const control = RESERVED_KEY_UI[contract.key]?.control ?? "singleDropdown";

  return (
    <div className={styles.labelBlock}>
      {control === "switcher" ? (
        <ContentSwitcher
          label={label}
          size="sm"
          options={options}
          value={values[0] ?? null}
          disabled={busy}
          onValueChange={(value) => onChange(value === null ? [] : [value])}
        />
      ) : control === "openMultiSelect" ? (
        <OpenLabelMultiInput
          labelKey={contract.key}
          selected={values}
          busy={busy}
          placeholder={`${label} 값을 입력해 주세요.`}
          onChange={onChange}
        />
      ) : (
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label={label}
          // 위 caption 이 축 이름을 이미 보여준다. 그래서 트리거에는 값만 표시한다.
          placeholder="선택 안 함"
          options={options}
          value={values[0] ?? null}
          disabled={busy}
          onValueChange={(value) => onChange(value === null ? [] : [value])}
        />
      )}
    </div>
  );
}

// 409 충돌 비교 mode. 좌는 "내 변경"(baseline→draft), 우는 "서버 변경"(baseline→최신)이다.
// 둘 다 read-only Before|After 도메인 diff 다. 자동/수동 rebase·force·retry 는 제공하지 않는다.
// 사용자가 자기 draft 를 명시적으로 폐기해야 최신값으로 다시 초기화된다.
function ConflictCompareView({
  myDraft,
  baseline,
  latest,
}: {
  myDraft: DraftConfiguration;
  baseline: DraftConfiguration;
  latest: ConfigurationView | null;
}): ReactNode {
  const latestDraft = draftFromConfiguration(latest);
  return (
    <>
      <div className={styles.state}>
        <Text variant={hatcheryTextModes["body-bold"]}>다른 사용자가 먼저 저장했습니다.</Text>
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
          내 변경과 서버의 최신 변경을 비교한 뒤, 내 변경을 폐기하고 최신값을 불러와 다시 편집해
          주세요.
        </Text>
      </div>
      <div className={styles.grid}>
        <ConfigDiffCard title="내 변경 내용을 확인합니다." before={baseline} after={myDraft} />
        <ConfigDiffCard
          title="서버 변경 내용을 확인합니다."
          before={baseline}
          after={latestDraft}
        />
      </div>
    </>
  );
}

// 한쪽 변경의 Before|After 도메인 diff. 값이 같은 축은 "변경 없음"으로 접는다. 그래서 차이만 눈에 띈다.
function ConfigDiffCard({
  title,
  before,
  after,
}: {
  title: string;
  before: DraftConfiguration;
  after: DraftConfiguration;
}): ReactNode {
  return (
    <section className={styles.card} aria-label={title}>
      <div className={styles.cardHead}>
        <Text variant={hatcheryTextModes["title-bold"]}>{title}</Text>
      </div>
      <DiffRows axes={configurationDiff(before, after)} showUnchanged />
    </section>
  );
}

// 라벨 축은 카드 넷으로 나뉜다. 그래서 `변경됨` 도 카드 단위로 판정한다. `configurationDiff` 의
// `라벨` 축은 넷을 한 줄로 합친 값이고, 아래 변경 요약이 쓴다. 그래서 카드마다 "내가 담은 key 가
// 바뀌었나" 를 여기서 따로 검사한다. 정규화 서명으로 비교하므로 대소문자·공백 차이는 변경이 아니다.
function labelsChangedFor(
  before: DraftConfiguration,
  after: DraftConfiguration,
  ownsKey: (key: string) => boolean,
): boolean {
  return labelSignatures(before, ownsKey) !== labelSignatures(after, ownsKey);
}

function labelSignatures(draft: DraftConfiguration, ownsKey: (key: string) => boolean): string {
  return draft.labels
    .filter((label) => ownsKey(label.key))
    .map(labelSignature)
    .sort()
    .join("|");
}

// 미해소 참조의 표시 이름. 종류별 ref shape 이 다르다(Embodiment/Tip=name, Label=key:value).
// 그래서 한 줄 문자열로 만든다.
function formatUnresolvedRef(reference: UnresolvedReference): string {
  const ref = reference.ref;
  if ("display_name" in ref) return ref.display_name;
  if ("key" in ref) {
    return `${ref.key}: ${ref.value}`;
  }
  return "tag" in ref && ref.tag !== null ? `${ref.name}:${ref.tag}` : ref.name;
}

// 액션 결과. 호스트가 다음 동작(상세 새로고침 · 창 닫기 · 큐 새로고침)을 정한다.
// confirmed·reverted 는 리뷰 큐에서 제거하는 동작이다. proposed·saved 뒤에는 같은 창에서 계속 볼
// 것이 있다.
export type DatasetReviewOutcome = "proposed" | "saved" | "confirmed" | "reverted";

export type DatasetReviewPanelProps = {
  review: DatasetReviewBinding;
  // 리뷰 근거 카드(메타데이터·에피소드 품질)의 소스. 상위 컴포넌트가 설정과 별개로 로드한다.
  details: LoadState<DatasetDetails>;
  onOutcome?: (outcome: DatasetReviewOutcome) => void;
  /**
   * 비어 있는 video fps 를 지정한다. 영상 영역은 카탈로그 fps 가 빈 데이터셋에만 이
   * 입력을 표시한다.
   *
   * 설정(embodiment·tip·label)과 다른 엔드포인트라서 draft·dirty·`expected_revision` 에 섞이지
   * 않는다. 호출과 그 뒤의 재조회는 호스트가 소유한다. 그래서 여기서는 callback 만 받는다.
   * 생략하면 카드는 읽기 전용이다(권한 미충족, 또는 소비처가 재조회를 못 하는 자리).
   */
  onDesignateVideoFps?: (fps: number) => Promise<void>;
  dataset: Dataset | null;
  structureState: LoadState<DatasetStructure>;
  // 좌측 표본 레인의 내용. 라벨을 확정하려면 실제 장면을 봐야 하는 축(환경·작업)이 있다.
  // 그래서 근거와 편집을 한 화면에 함께 둔다. 자리(폭·접힘·스크롤 밖)는 이 패널이 소유하고,
  // 내용은 소비처가 넣는다. 에피소드 조회 endpoint 는 리뷰 계약이 아니라 파일 브라우저 소스에
  // 속한다. 그래서 이 패널은 그 endpoint 를 알 필요가 없다. 생략하면 레인 자리를 만들지 않는다.
  sample?: ReactNode;
};

/**
 * 데이터셋 리뷰 본문. 사용자는 판단 근거를 읽고 같은 화면에서 설정을 확정한다.
 *
 * 이 화면은 리뷰하는 사람만 쓰지 않는다. "이 데이터셋이 어떤 상태인가"를 보는 화면이기도 하다.
 * 그래서 편집 축 옆에 읽기 전용 근거(메타데이터 · 에피소드 품질)를 함께 둔다.
 * 기본 정보와 수집 설정, 라벨과 품질을 각각 한 행에 묶는다.
 * 검사·처리 상태는 짧은 상단 줄, 채널·카메라는 폼 전체 폭에서 표시한다.
 *
 * 상세 창과 라우트 페이지의 `메타 정보` 탭이 이 패널을 마운트한다. 창 셸·이탈 가드·탭 전환은
 * 소유하지 않는다. 호스트는 `config.snapshot.dirty` 로 dirty 를 읽는다. 저장은 draft 전체를 한 번의
 * PUT(또는 review 확정)으로 보낸다. 필드별 즉시 저장 경로는 없다.
 *
 * 기본 정보와 장비·라벨은 같은 draft를 편집한다. 읽기 전용 기술 정보는 전체 폭에 두고,
 * 하단 액션이 모든 설정 변경을 함께 저장한다.
 */
/**
 * 후보를 남긴 사람과 시각을 한 줄로 쓴다. 검토 요청이면 요청자, 저장이 남긴 직전 값이면 저장한
 * 사람이다. 요청자는 비어 있을 수 있다(멤버 삭제 시 SET NULL). 그래서 시각만 있어도 뜻이 통하게
 * 쓴다.
 */
function formatRequestOrigin(candidate: ConfigCandidateView): string {
  const at = formatDateTime(parseUtcMs(candidate.modified_at));
  const who = candidate.proposed_by?.display_name ?? "알 수 없는 사용자";
  return `${who} · ${at}`;
}

/**
 * 액션 버튼. 버튼 이름과 툴팁이 이 화면이 하는 일의 범위를 알린다.
 *
 * - 상세의 `검토 요청` 은 적용된 설정을 바꾸지 않고 후보만 보낸다.
 * - 상세의 `저장` 은 곧바로 적용한다(생성자·admin).
 * - 리뷰의 `확정` 은 폼의 값을 적용하고 큐에서 제거한다.
 * - 리뷰의 `직전 값으로 되돌리기` 는 저장이 덮은 값을 다시 적용하고 큐에서 제거한다.
 * 기본 정보와 장비·라벨 변경은 같은 액션으로 제출한다.
 */
function ReviewActionButton({
  text,
  tooltip,
  variant,
  disabled,
  onClick,
}: {
  text: string;
  tooltip: string;
  variant: "solid" | "surface";
  disabled: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <HatTextTooltip text={tooltip}>
      <IconButton text={text} size="md" variant={variant} disabled={disabled} onClick={onClick} />
    </HatTextTooltip>
  );
}

export function DatasetReviewPanel({
  review,
  details,
  onOutcome,
  onDesignateVideoFps,
  dataset,
  structureState,
  sample,
}: DatasetReviewPanelProps): ReactNode {
  const videoInfoId = useId();
  const { controller, snapshot, mode, embodiments, tips, enabled } = review;
  const { draft, busy, loadState, duplicateLabels, conflict, candidate } = snapshot;
  // 어떤 액션을 허용할지는 서버가 계산한 자격이 정한다. 저장·확정·fps·수집 방식은 생성자·admin,
  // 검토 요청은 모든 member 다. 둘 다 없으면(로드 전) 편집을 잠그고 액션 행을 숨긴다.
  const canWrite = snapshot.permissions?.can_write === true;
  const canEdit = canWrite || snapshot.permissions?.can_propose === true;

  if (!enabled) {
    return null;
  }

  if (loadState === "loading") {
    return <LoadingState size="lg">Dataset 설정을 불러오고 있습니다.</LoadingState>;
  }

  if (loadState === "error") {
    return (
      <EmptyState size="lg" tone="error">
        {`Dataset 설정을 불러오지 못했습니다. ${loadFailureReason(snapshot.loadError)}`.trim()}
        <br />
        {RETRY_LATER}
      </EmptyState>
    );
  }

  // 계약이 내려준 reserved key 만 전용 컨트롤로 노출한다(순서는 RESERVED_KEYS 고정).
  const reservedContracts = RESERVED_KEYS.map((key) =>
    snapshot.contracts.find((contract) => contract.key === key),
  ).filter((contract): contract is ReservedKeyContract => contract !== undefined);
  const reservedKeySet = new Set(reservedContracts.map((contract) => contract.key));
  // 값이 하나뿐인 예약 축(환경·출처)은 한 카드에 나란히 담는다. 각각 카드를 가지면 오른쪽 열만
  // 카드 다섯 개로 길어져 좌우 열 길이 차이가 커진다. 여러 값을 받는 축(작업)은 입력이 세로로
  // 늘어나므로 자기 카드를 가진다. 구분 기준을 key 목록이 아니라 컨트롤 종류로 둔다. 그러면
  // 계약이 축을 추가해도 자리가 자동으로 정해진다.
  const compactContracts = reservedContracts.filter(
    (contract) => reservedControlKind(contract.key) === "singleDropdown",
  );
  const expandedContracts = reservedContracts.filter(
    (contract) => reservedControlKind(contract.key) !== "singleDropdown",
  );
  // `그 외 라벨` 에는 예약 축 밖의 라벨만 남는다(generic manual · worker 산출).
  const otherLabels = draft.labels.filter((label) => !reservedKeySet.has(label.key.toLowerCase()));
  const embodimentOptions: SingleSelectDropdownOption[] = embodiments.map((entry) => ({
    value: entry.uuid,
    label: entry.name,
  }));
  const tipOptions: SingleSelectDropdownOption[] = tips.map((entry) => ({
    value: entry.uuid,
    label: formatTip(entry),
  }));
  const selectedEmbodiment =
    draft.embodiment !== null
      ? (embodiments.find((entry) => entry.uuid === draft.embodiment?.uuid) ?? null)
      : null;
  // 편집 컨트롤 잠금. 저장 중이거나 쓰기 권한이 없으면 잠근다.
  const locked = busy || !canEdit;
  // 어느 카드가 바뀌었는지 계산한다. 축별 before/after 는 아래 변경 요약이 보여준다.
  const configAxes = configurationDiff(snapshot.baselineDraft, draft);
  const embodimentChanged = configAxes.some(
    (entry) => entry.axis === DIFF_AXIS.embodiment && entry.changed,
  );
  const tipChanged = configAxes.some((entry) => entry.axis === DIFF_AXIS.tip && entry.changed);
  // 라벨 카드마다 자기가 담은 key 만 본다. `그 외 라벨` 은 전용 축이 표시하지 않는 나머지다.
  const compactKeys = new Set(compactContracts.map((contract) => contract.key));
  const compactChanged = labelsChangedFor(snapshot.baselineDraft, draft, (key) =>
    compactKeys.has(key.toLowerCase()),
  );
  const otherLabelsChanged = labelsChangedFor(
    snapshot.baselineDraft,
    draft,
    (key) => !reservedKeySet.has(key.toLowerCase()),
  );

  const run = (action: () => Promise<boolean>, outcome: DatasetReviewOutcome): void => {
    void action().then((ok) => {
      if (ok) {
        onOutcome?.(outcome);
      }
    });
  };
  // 저장이 남긴 직전 값. 요청이 아니라 되돌리기에 쓰는 값이다. 리뷰 창에서만 표시한다. 상세에서
  // 표시하면 방금 저장한 사람에게 "요청이 올라와 있다" 고 알리게 된다.
  const previousActive = candidate?.origin === "previous_active" ? candidate : null;
  const previousAxes =
    previousActive === null
      ? []
      : configurationDiff(
          draftFromConfiguration(previousActive.configuration),
          snapshot.baselineDraft,
        );

  return (
    <div className={styles.panel} data-sample={sample === undefined ? "false" : "true"}>
      {/* 스크롤은 이 안에서만 일어난다. 아래 변경 요약·액션 행은 본문 길이와 관계없이 고정된다.
          c6 `Scrollbar` 가 thin themed thumb·min-size 0·overscroll 격리를 함께 제공한다
          (FileBrowser·EpisodeList 와 같은 방식). gutter 는 auto 다. 카드는 이미 테두리로 구분된다.
          스크롤바가 없을 때 빈 띠가 보이면 우열 폭만 맞지 않아 보인다. */}
      <Scrollbar className={styles.scrollArea} gutter="auto">
        {conflict !== null ? (
          <ConflictCompareView
            myDraft={draft}
            baseline={snapshot.baselineDraft}
            latest={conflict.latestPair.active}
          />
        ) : (
          <>
            {/* 후보가 가리키던 카탈로그 항목이 삭제된 경우. 서버가 GET 시점에 알려준다.
              사용자가 아래 컨트롤에서 대체값을 직접 고른다. */}
            {snapshot.unresolvedReferences.length > 0 ? (
              <div className={styles.state}>
                <Text variant={hatcheryTextModes["body-bold"]} className={styles.warn}>
                  삭제된 항목이 있습니다.
                </Text>
                <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
                  {snapshot.unresolvedReferences
                    .map((reference) => `${reference.kind}: ${formatUnresolvedRef(reference)}`)
                    .join(", ")}{" "}
                  — 대체할 값을 지정한 뒤 저장해 주세요.
                </Text>
              </div>
            ) : null}
            {/* 받은 변경 요청. 아래 폼은 active 가 아니라 이 요청의 값을 가진다. 누가 언제
              보냈는지 표시해야 리뷰어가 "이걸 확정할 것인가" 를 판단할 수 있다. 상세에서는
              사용자가 다른 사람의 요청을 이어서 고치는 중이라는 것을 알아야 한다. */}
            {isUserProposal(candidate) ? (
              <div className={styles.state}>
                <Text variant={hatcheryTextModes["body-bold"]}>
                  {mode === "candidate"
                    ? "올라온 변경 요청입니다."
                    : "이 Dataset 에 변경 요청이 올라와 있습니다."}
                </Text>
                <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
                  {formatRequestOrigin(candidate)}
                  {mode === "candidate"
                    ? " — 값을 고쳐서 확정할 수 있습니다."
                    : " — 아래 값이 그 요청이고, 이어서 고쳐 다시 올릴 수 있습니다."}
                </Text>
              </div>
            ) : null}
            {/* 저장된 설정. 폼은 이미 적용된 값이다. 무엇을 덮었는지 보여 줘야 리뷰어가 "그대로
              둘지, 되돌릴지" 를 고를 수 있다. */}
            {mode === "candidate" && previousActive !== null ? (
              <div className={styles.state}>
                <Text variant={hatcheryTextModes["body-bold"]}>저장된 설정입니다.</Text>
                <Text variant={hatcheryTextModes["body-normal"]} className={styles.muted}>
                  {formatRequestOrigin(previousActive)} — 직전 값과 비교해 확정하거나 되돌릴 수
                  있습니다.
                </Text>
                <ChangeSummary
                  title="저장으로 이렇게 바뀌었습니다."
                  axes={previousAxes}
                  columns={{ before: "직전 값", after: "현재 값" }}
                />
              </div>
            ) : null}
            {/* 관련 정보끼리 행을 공유한다. 기술 정보는 두 열 전체를 써서 마지막 열에 쌓이지 않는다. */}
            <div className={styles.bodyGrid}>
              <div className={styles.statusRow}>
                <MetadataCard
                  state={details}
                  videoFpsTarget={
                    details.status === "success" && needsVideoFps(details.data)
                      ? videoInfoId
                      : undefined
                  }
                />
                <DatasetProcessingCard processing={dataset?.processing ?? null} />
              </div>
              <div className={styles.pair}>
                <DatasetInfoCard dataset={dataset} details={details} review={review} />
                <section className={styles.card} aria-label="수집 설정">
                  <div className={styles.cardHead}>
                    <CardTitle>
                      수집 설정
                      <ModifiedMark changed={compactChanged || embodimentChanged || tipChanged} />
                    </CardTitle>
                  </div>
                  {compactContracts.map((contract) => (
                    <Field
                      key={contract.key}
                      label={reservedKeyLabel(contract.key)}
                      layout="inline"
                      labelWidth="5rem"
                      disabled={locked}
                    >
                      <ReservedLabelControl
                        contract={contract}
                        values={labelsForKey(draft, contract.key).map((label) => label.value)}
                        busy={locked}
                        onChange={(values) =>
                          controller.setLabelsForKey(
                            contract.key,
                            values.map((value) => toDraftLabel(contract.key, value)),
                          )
                        }
                      />
                    </Field>
                  ))}
                  <section className={styles.setting} aria-label="Embodiment">
                    <Field label="Embodiment" layout="inline" labelWidth="5rem" disabled={locked}>
                      <SingleSelectDropdown
                        mode={hatcheryDropdownModes.default}
                        label="Embodiment"
                        value={draft.embodiment?.uuid ?? null}
                        options={embodimentOptions}
                        placeholder="Embodiment를 선택해 주세요."
                        searchable
                        disabled={locked}
                        onValueChange={(uuid) => {
                          const picked = embodiments.find((entry) => entry.uuid === uuid);
                          if (picked)
                            controller.setEmbodiment({ uuid: picked.uuid, name: picked.name });
                        }}
                      />
                    </Field>
                    {selectedEmbodiment ? (
                      <details className={styles.embodimentDetails}>
                        <summary>장비 상세</summary>
                        <EmbodimentDetailContent embodiment={selectedEmbodiment} flush />
                      </details>
                    ) : null}
                  </section>
                  <section className={styles.setting} aria-label="팁">
                    <Field label="팁" layout="inline" labelWidth="5rem" disabled={locked}>
                      <SingleSelectDropdown
                        mode={hatcheryDropdownModes.default}
                        label="팁"
                        value={draft.tip?.uuid ?? null}
                        options={tipOptions}
                        placeholder="팁을 선택해 주세요."
                        searchable
                        disabled={locked}
                        onValueChange={(uuid) => {
                          const picked = tips.find((entry) => entry.uuid === uuid);
                          controller.setTip(
                            picked
                              ? { uuid: picked.uuid, name: picked.name, tag: picked.tag }
                              : null,
                          );
                        }}
                      />
                    </Field>
                  </section>
                </section>
              </div>
              <div className={styles.pair}>
                <section className={styles.card} aria-label="라벨">
                  {/* 작업과 일반 라벨은 같은 카드 안에서 각자의 편집 영역을 가진다. */}
                  {expandedContracts.map((contract) => {
                    const axisLabel = reservedKeyLabel(contract.key);
                    return (
                      <section
                        key={contract.key}
                        className={styles.labelSection}
                        aria-label={axisLabel}
                      >
                        <div className={styles.cardHead}>
                          <CardTitle>
                            {axisLabel}
                            <ModifiedMark
                              changed={labelsChangedFor(
                                snapshot.baselineDraft,
                                draft,
                                (key) => key.toLowerCase() === contract.key,
                              )}
                            />
                          </CardTitle>
                        </div>
                        <div
                          className={styles.axisGroup}
                          role="group"
                          aria-label={`${axisLabel} 축`}
                        >
                          <ReservedLabelControl
                            contract={contract}
                            values={labelsForKey(draft, contract.key).map((label) => label.value)}
                            busy={locked}
                            onChange={(values) =>
                              controller.setLabelsForKey(
                                contract.key,
                                values.map((value) => toDraftLabel(contract.key, value)),
                              )
                            }
                          />
                        </div>
                      </section>
                    );
                  })}

                  {/* 예약 축 밖의 라벨은 칩 목록과 추가 행으로 편집한다. */}
                  <section className={styles.labelSection} aria-label="그 외 라벨">
                    <div className={styles.cardHead}>
                      <CardTitle>
                        그 외 라벨
                        <ModifiedMark changed={otherLabelsChanged} />
                      </CardTitle>
                    </div>
                    {otherLabels.length > 0 ? (
                      <div className={styles.chips}>
                        {otherLabels.map((label) => (
                          <span key={labelSignature(label)} className={styles.chipRow}>
                            <HatLabelChip
                              labelKey={label.key}
                              value={label.value}
                              color={label.kind === "existing" ? label.color : 0}
                            />
                            {canEdit ? (
                              <IconButton
                                text="삭제"
                                size="sm"
                                variant="surface"
                                disabled={locked}
                                aria-label={`${label.key}: ${label.value} 삭제`}
                                onClick={() => controller.removeLabel(label)}
                              />
                            ) : null}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
                        아직 추가한 라벨이 없습니다.
                      </Text>
                    )}

                    {canEdit ? (
                      <AddLabelRow
                        contracts={snapshot.contracts}
                        busy={locked}
                        // 에피소드 전용 축을 넣으면 설정 저장 전체가 거절되므로 입력에서 막는다.
                        blockedKeys={[...RESERVED_KEYS, ...EPISODE_LABEL_KEYS]}
                        assignedLabels={draft.labels.map((label) => ({
                          key: label.key,
                          value: label.value,
                        }))}
                        onAdd={(key, value) => {
                          controller.addLabel(toDraftLabel(key, value));
                          return Promise.resolve();
                        }}
                      />
                    ) : null}
                  </section>

                  {/* 중복 경고는 전체 라벨 묶음에서 한 번만 표시한다. */}
                  {duplicateLabels.length > 0 ? (
                    <Text variant={hatcheryTextModes["body-normal"]} className={styles.warn}>
                      중복된 라벨이 있습니다:{" "}
                      {duplicateLabels.map((l) => `${l.key}: ${l.value}`).join(", ")}
                    </Text>
                  ) : null}
                </section>
                <EpisodeQualityCard state={details} />
              </div>
              <DatasetOverviewPanel
                state={details}
                structureState={structureState}
                videoInfoId={videoInfoId}
                onDesignateVideoFps={canWrite ? onDesignateVideoFps : undefined}
              />
            </div>
          </>
        )}
      </Scrollbar>

      {/* 표본 레인. 폼과 형제이고 스크롤 영역 밖에 있다. 이 화면은 근거를 보며 라벨을 채우는
          화면이다. 폼을 아래로 스크롤했을 때 영상이 사라지면 이 배치의 전제가 깨진다. */}
      {sample === undefined ? null : (
        <section className={styles.sample} aria-label="표본 영상">
          {sample}
        </section>
      )}

      {/* 무엇이 바뀌는지 모아 보여준다. 설정 축만 보여준다. 정보는 카드가 따로 표시한다.
          두 값의 성격이 달라서 열 이름을 붙인다.
          - 왼쪽: 지금 적용된 값.
          - 오른쪽: 이 화면이 보내거나 확정할 값. */}
      {conflict === null ? (
        <div className={styles.summaryRow}>
          <ChangeSummary
            title={
              mode === "candidate"
                ? "확정하면 이렇게 바뀝니다."
                : canWrite
                  ? "저장하거나 요청하면 이렇게 바뀝니다."
                  : "요청하면 이렇게 바뀝니다."
            }
            axes={configAxes}
            columns={{
              before: "이전",
              after: canWrite && mode === "direct" ? "변경" : "변경 요청",
            }}
          />
        </div>
      ) : null}

      {/* 쓰기 자격이 없으면 액션 행 전체를 숨긴다. 충돌 중에는 저장 대신 폐기 경로만 제공한다.
          자동 rebase·force 는 제공하지 않는다. */}
      {!canEdit ? null : conflict !== null ? (
        <div className={styles.actions}>
          <IconButton
            text="내 변경 폐기하고 최신값 불러오기"
            size="md"
            variant="solid"
            onClick={() => controller.adoptLatestFromConflict()}
          />
        </div>
      ) : mode === "candidate" ? (
        canWrite ? (
          <div className={styles.actions}>
            {/* 잘못 보낸 요청은 리뷰에서 값을 고쳐 확정해서 취소한다. 별도 폐기 버튼은 두지
                않는다. 저장된 설정만 직전 값으로 되돌릴 수 있다. */}
            {previousActive !== null ? (
              <ReviewActionButton
                text={busy ? "되돌리는 중…" : "직전 값으로 되돌리기"}
                tooltip={
                  controller.canRevert
                    ? "저장이 덮은 직전 설정을 다시 적용하고 리뷰 큐에서 내립니다."
                    : "고친 값이 있으면 되돌릴 수 없습니다. 먼저 편집을 취소해 주세요."
                }
                variant="surface"
                disabled={!controller.canRevert}
                onClick={() => run(controller.revertToPrevious, "reverted")}
              />
            ) : null}
            <ReviewActionButton
              text={busy ? "확정 중…" : "확정"}
              tooltip="이 설정을 Dataset 에 적용하고 리뷰 큐에서 내립니다."
              variant="solid"
              disabled={!controller.canConfirm}
              onClick={() => run(controller.confirmCandidate, "confirmed")}
            />
          </div>
        ) : null
      ) : (
        <div className={styles.actions}>
          {canWrite && isUserProposal(candidate) ? (
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.muted}>
              저장하면 올라와 있던 검토 요청이 이 설정으로 대체됩니다.
            </Text>
          ) : null}
          <ReviewActionButton
            text={busy ? "요청 중…" : "검토 요청"}
            tooltip="이 설정으로 바꿔 달라고 리뷰에 올립니다. 적용된 설정은 그대로입니다."
            variant={canWrite ? "surface" : "solid"}
            disabled={!controller.canPropose}
            onClick={() => run(controller.propose, "proposed")}
          />
          {canWrite ? (
            <ReviewActionButton
              text={busy ? "저장 중…" : "저장"}
              tooltip="이 설정을 Dataset 에 바로 적용합니다. 직전 값은 리뷰에서 되돌릴 수 있도록 남습니다."
              variant="solid"
              disabled={!controller.canSave}
              onClick={() => run(controller.save, "saved")}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
